const DAY_MS = 86_400_000

export interface DateSpan {
  /** First actionable day (the requested start, or today if that is earlier). */
  start: string
  end: string
  /** Inclusive count of actionable days. Zero when the span is inverted. */
  days: number
  /** How many requested days fell before today and are therefore unwritable. */
  skippedPastDays: number
}

const midnight = (iso: string) => new Date(`${iso}T00:00:00`).getTime()

/**
 * Resolve a requested date range against today's floor.
 *
 * Purely calendar arithmetic — it counts days, never money. It lives here rather
 * than inside DayDetailEditPrices so that component's source stays free of any
 * `Math.*` call at all, keeping the R-FE-MATH source guard a strict, literal
 * check instead of one with carve-outs to argue about.
 */
export function resolveDateSpan(startDate: string, endDate: string, today: string): DateSpan {
  // ISO dates sort lexicographically, so "the later of the two" is a string
  // compare. The previous version took a numeric max of two LOCAL midnights and
  // formatted the winner with toISOString() — which is UTC, and local midnight
  // is the PREVIOUS day in UTC anywhere east of Greenwich. In Cairo (UTC+3) a
  // span starting 26 July was reported as starting the 25th.
  const start = startDate > today ? startDate : today

  // Millisecond arithmetic is still the honest way to COUNT days (it survives
  // month ends and DST); it just must never decide what a date is called.
  const startMs = midnight(start)
  const endMs = midnight(endDate)
  const askedMs = midnight(startDate)
  const floorMs = midnight(today)

  return {
    start,
    end: endDate,
    days: endMs < startMs ? 0 : Math.round((endMs - startMs) / DAY_MS) + 1,
    skippedPastDays: Math.max(0, Math.round((floorMs - askedMs) / DAY_MS)),
  }
}
